import React, { useEffect, useState } from 'react';
import { Box, Heading, Text, Card, CardBody, HStack, Badge, VStack, Table, Thead, Tbody, Tr, Th, Td, Button, Input, InputGroup, InputLeftElement, Icon } from '@chakra-ui/react';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../services/api';
import { Link as RouterLink } from 'react-router-dom';
import { FiSearch } from 'react-icons/fi';
import { statusColor, statusKeyPath } from '../utils/status';

const ProjectManagement = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    (async () => {
      const res = await api.get(`/api/proposals/projects-overview?q=${encodeURIComponent(q)}`);
      const raw = res?.data?.data?.projects || [];
      // Filter only commissioned projects; pick commissioned version as display
      const onlyCommissioned = raw
        .map((p) => {
          const all = [p.parent, ...(p.versions || [])];
          const commissioned = all
            .filter(v => String(v.status||'').toLowerCase() === 'commissioned')
            .sort((a,b)=> (Number(b.version_number||0) - Number(a.version_number||0)) || (new Date(b.created_at) - new Date(a.created_at)))[0];
          return commissioned ? { ...p, __display: commissioned } : null;
        })
        .filter(Boolean);
      setProjects(onlyCommissioned);
    })();
  }, [q]);

  const currencySymbol = (code) => ({ EUR: '€', USD: '$', GBP: '£', TRY: '₺' }[String(code||'').toUpperCase()] || '€');
  const getStatusColor = statusColor;
  const rowBgEven = 'gray.50';
  const rowBgOdd = 'gray.100';

  return (
    <Box>
      <Heading size="lg" mb={4}>{t('nav.project_management')}</Heading>
      <HStack mb={4}>
        <InputGroup maxW="360px">
          <InputLeftElement pointerEvents="none"><Icon as={FiSearch} color="gray.400" /></InputLeftElement>
          <Input placeholder={t('common.search')} value={q} onChange={(e)=> setQ(e.target.value)} />
        </InputGroup>
      </HStack>
      <VStack align="stretch" spacing={4}>
        {projects.map((p) => (
          <Card key={p.parent.id}>
            <CardBody>
              <VStack align="stretch" spacing={3}>
                {/* Header row: commissioned version as header */}
                <HStack justify="flex-start" align="flex-start">
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="bold">#{(p.__display||p.parent).id} — {(p.__display||p.parent).title}</Text>
                    <HStack>
                      <Badge colorScheme={getStatusColor((p.__display||p.parent).status)}>
                        {t(statusKeyPath((p.__display||p.parent).status))}
                      </Badge>
                      <Text color="gray.600">{(p.__display||p.parent).client_company || (p.__display||p.parent).client_name}</Text>
                    </HStack>
                  </VStack>
                </HStack>

                {/* Versions inline (include parent as v1 when no children) */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>{t('proposals.versions')}</Text>
                  <Table size="sm" variant="simple">
                    <Thead><Tr><Th>ID</Th><Th>{t('proposals.version')}</Th><Th isNumeric>{t('proposals.total')}</Th><Th>{t('proposals.status')}</Th><Th></Th></Tr></Thead>
                    <Tbody>
                      {(() => {
                        const combined = [
                          { id: p.parent.id, version_number: p.parent.version_number || 1, total_amount: p.parent.total_amount, status: p.parent.status, isParent: true },
                          ...(p.versions || [])
                        ];
                        return combined.map((v, idx) => (
                          <Tr key={`${v.isParent?'parent':'child'}-${v.id}`} bg={idx % 2 === 0 ? rowBgEven : rowBgOdd}>
                            <Td>#{v.id}</Td>
                            <Td>v{v.version_number || (idx === 0 ? 1 : '')}</Td>
                            <Td isNumeric>{currencySymbol(p.parent.currency)}{Number(v.total_amount||0).toLocaleString()}</Td>
                            <Td><Badge colorScheme={getStatusColor(v.status)}>{t(statusKeyPath(v.status))}</Badge></Td>
                            <Td><Button as={RouterLink} to={`/proposals/${v.id}/edit`} size="xs" variant="outline">{t('common.edit')}</Button></Td>
                          </Tr>
                        ));
                      })()}
                    </Tbody>
                  </Table>
                </Box>

                {/* Invoices inline */}
                {p.invoices_list?.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>{t('invoices.title')}</Text>
                    <Table size="sm" variant="simple">
                      <Thead><Tr><Th>No</Th><Th>{t('proposals.status')}</Th><Th>{t('invoices.due')}</Th><Th isNumeric>{t('proposals.total')}</Th><Th></Th></Tr></Thead>
                      <Tbody>
                        {p.invoices_list.map((inv, idx) => (
                          <Tr key={inv.id} bg={idx % 2 === 0 ? rowBgEven : rowBgOdd}>
                            <Td>{inv.invoice_number}</Td>
                            <Td><Badge colorScheme={getStatusColor(inv.status)}>{t(statusKeyPath(inv.status))}</Badge></Td>
                            <Td>{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'}</Td>
                            <Td isNumeric>{currencySymbol(p.parent.currency)}{Number(inv.total_amount||0).toLocaleString()}</Td>
                            <Td><Button as={RouterLink} to={`/invoices/${inv.id}`} size="xs" variant="outline">{t('common.view')}</Button></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}

                {/* Financial summary */}
                <HStack justify="space-between">
                  <Text>{t('proposals.total')}: <b>{currencySymbol(p.parent.currency)}{Number(p.parent.total_amount||0).toLocaleString()}</b></Text>
                  <Text>{t('invoices.title')}: <b>{currencySymbol(p.parent.currency)}{Number(p.invoices.total_invoiced||0).toLocaleString()}</b> ({p.invoices.count})</Text>
                  <Text>{t('projects.purchase_total')}: <b>{currencySymbol(p.parent.currency)}{Number(p.purchase_total||0).toLocaleString()}</b></Text>
                  <Text>{t('projects.balance')}: <b>{currencySymbol(p.parent.currency)}{Number((p.invoices.total_invoiced||0) - (p.purchase_total||0)).toLocaleString()}</b></Text>
                </HStack>

                {/* Suppliers */}
                {p.suppliers?.length > 0 && (
                  <Box>
                    <Text fontWeight="semibold" mb={1}>{t('nav.suppliers')}</Text>
                    <HStack spacing={2} wrap="wrap">
                      {p.suppliers.map(s => (<Badge key={s.id} colorScheme="gray">{s.name}</Badge>))}
                    </HStack>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        ))}
      </VStack>
    </Box>
  );
};

export default ProjectManagement;


